import {
  EnvConfig,
  normalizeUrl,
  OrderByInput,
  useFileListOrder,
  useSetFileListOrder,
} from '@gripp/shared-logic';
import { Colors, ImageLightBox, InfiniteDataGrid } from '@gripp/shared-ui';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { useMaxHeight } from '../../shared/hooks/useMaxHeight';
import { CreateFileDropdown } from './createFileDropdown';
import { EditFileMenu } from './editFileMenu';
import { useColumnsFile } from './useColumnsFile';

type FilesListProps = {
  filter: any;
  showCheckboxColumn: boolean;
  countButton: (data: any) => React.JSX.Element;
  isProducerWorkspace?: boolean;
};

export const FilesList = ({
  filter,
  showCheckboxColumn,
  countButton,
  isProducerWorkspace = false,
}: FilesListProps) => {
  const { containerMaxHeight, listMaxHeight } = useMaxHeight(64, 0, 90);
  const { width } = useWindowDimensions();

  const { data: fileOrder } = useFileListOrder();
  const { mutateAsync: setFileOrder } = useSetFileListOrder();

  const [filesSelected, setFilesSelected] = useState<string[] | undefined>(
    undefined
  );
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>('');

  const showFileMenu = useMemo(() => {
    return filesSelected?.length === 1 ? true : false;
  }, [filesSelected]);

  const updateFileSelection = (rows: any[]) => {
    if (rows && rows.length > 0) {
      setFilesSelected(rows.map((row) => row.id));
    } else {
      setFilesSelected(undefined);
    }
  };

  const onOrderByChange = (orderBy: OrderByInput) => {
    setFileOrder(orderBy);
  };

  const handleFileNameClick = async (file: any) => {
    const mediaBaseUrl = EnvConfig.mediaUrl;
    const apiBaseUrl = EnvConfig.apiUrl;

    if (file.type === 'IMAGE' && file.extension) {
      const fileUrl = `${mediaBaseUrl}/images/${file.id}/original.${file.extension}`;
      setImageUrl(fileUrl);
      setImageModalVisible(true);
    } else if (file.type === 'DOCUMENT') {
      const documentUrl = `${apiBaseUrl}/document/${file.id}`;
      await openDocumentInBrowser(documentUrl, file);
    } else if (file.type === 'LINK' && file.url) {
      const normalizedUrl = normalizeUrl(file.url);
      window.open(normalizedUrl, '_blank');
    } else {
      const fileUrl = `${mediaBaseUrl}/media/${file.id}`;
      await openFileInBrowser({
        url: fileUrl,
        name: file.name,
        type: file.type,
      });
    }
  };

  const openFileInBrowser = async (media: any) => {
    if (!media.url) {
      return;
    }
    window.open(media.url, '_blank');
  };

  const openDocumentInBrowser = async (documentUrl: string, file: any) => {
    const response = await fetch(documentUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();

      if (data.downloadUrl) {
        const fileResponse = await fetch(data.downloadUrl);

        if (fileResponse.ok) {
          const fileBlob = await fileResponse.blob();

          const downloadLink = document.createElement('a');
          downloadLink.href = URL.createObjectURL(fileBlob);
          downloadLink.download = file.name || 'document';
          downloadLink.style.display = 'none';
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);

          URL.revokeObjectURL(downloadLink.href);
        } else {
          window.open(data.downloadUrl, '_blank');
        }
      } else {
        window.open(documentUrl, '_blank');
      }
    } else {
      window.open(documentUrl, '_blank');
    }
  };

  const columns = useColumnsFile(
    showCheckboxColumn,
    countButton,
    isProducerWorkspace,
    handleFileNameClick
  ) as ColumnDef<any, any>[];

  const RenderEditFileMenu = () =>
    showFileMenu && filesSelected ? (
      <EditFileMenu fileId={filesSelected[0]} label="Edit File" />
    ) : (
      <CreateFileDropdown />
    );

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <View style={[styles.textWrapper, { maxWidth: width - 10 }]}>
        <Text style={styles.emptyTapText} numberOfLines={2}>
          Tap '+ Create File' to add your first file.
        </Text>
      </View>
    </View>
  );

  return (
    <View
      style={[
        styles.container,
        {
          maxHeight: containerMaxHeight,
        },
      ]}
    >
      <InfiniteDataGrid
        columns={columns}
        query={LIST_FILES}
        dataPrefix="media.listFiles"
        modelName="media"
        orderBy={fileOrder}
        pageSize={50}
        filter={filter}
        hideHeader={false}
        listMaxHeight={listMaxHeight}
        showScrollIndicator={true}
        onOrderByChange={onOrderByChange}
        textEmpty={renderEmpty()}
        showEmptyTable
        updateRowsSelected={(rows) => updateFileSelection(rows)}
        headerContent={RenderEditFileMenu()}
        exportFileName="files-export"
      />

      <ImageLightBox
        url={imageUrl}
        isOpened={imageModalVisible}
        onClose={() => setImageModalVisible(false)}
      />
    </View>
  );
};

const LIST_FILES = `
  query FindFiles($filter: ListFilesFilter, $page: PageRequest, $orderBy: OrderByInput) {
    media {
      listFiles(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        limit
        offset
        items {
          id
          name
          productsCount
          assetsCount
          addonsCount
          categories {
            id
            name
          }
          updatedAt
          createdAt
          type
          extension
          url
        }
      }
    }
  }
`;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: -1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  textWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTapText: {
    fontSize: 22,
    fontWeight: '700',
    color: Colors.blackText,
    textAlign: 'center',
  },
});
